import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { ArrowRight, BarChart3, CheckCircle2, ShieldCheck, Layers, Database, LineChart, FileSearch } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";

export default function Home() {
  const { t, i18n } = useTranslation();
  const { lang = "ar" } = useParams();
  const { hash } = useLocation();

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    document.documentElement.lang = lang === "ar" ? "es-AR" : "en";
    document.title = t("meta.title");
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", t("meta.description"));
  }, [lang, i18n, t]);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    // Retry briefly to allow layout to settle after route change
    let tries = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
      } else if (tries++ < 10) {
        setTimeout(tick, 30);
      }
    };
    tick();
  }, [hash]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A2540] text-white">
        <div className="absolute inset-0 opacity-25">
          <img
            src="/images/hero-lithium.jpg"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-[#0A2540]/85 to-[#0A2540]/55" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-12 lg:gap-12 lg:px-10">
          <div className="lg:col-span-7">
            <span className="inline-block rounded-sm border border-[#C85A32]/60 bg-[#C85A32]/10 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-[#F4A582]">
              {t("hero.kicker")}
            </span>
            <h1 className="mt-6 font-serif text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              {t("hero.headline")}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
              {t("hero.lead")}
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
              {t("hero.lead2")}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="#newsletter"
                data-testid="hero-primary-cta"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#C85A32] px-6 py-3 text-sm font-semibold text-white hover:bg-[#B04D28]"
              >
                {t("hero.primaryCta")} <ArrowRight size={16} />
              </a>
              <a
                href="#why"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-400 bg-transparent px-6 py-3 text-sm font-semibold text-white hover:bg-white/5"
              >
                {t("hero.secondaryCta")}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-slate-900 shadow-xl md:p-8">
              <div className="flex items-center gap-2">
                <BarChart3 size={16} className="text-[#0A2540]" />
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
                  {t("hero.statsTitle")}
                </p>
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-5">
                {["s1", "s2", "s3", "s4"].map((k) => (
                  <div key={k} className="border-l-4 border-[#C85A32] pl-3">
                    <dt className="text-xs text-slate-500">
                      {t(`hero.stats.${k}_label`)}
                    </dt>
                    <dd className="mt-1 font-serif text-2xl font-bold text-[#0A2540]">
                      {t(`hero.stats.${k}_value`)}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 overflow-hidden rounded-md">
                <img
                  src="/images/analytics.jpg"
                  alt=""
                  className="h-32 w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="bg-[#F8FAFC] px-4 py-20 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <p className="font-mono text-xs uppercase tracking-widest text-[#C85A32]">
            {t("why.eyebrow")}
          </p>
          <h2 className="mt-3 max-w-3xl font-serif text-2xl font-bold tracking-tight text-[#0A2540] sm:text-3xl lg:text-4xl">
            {t("why.title")}
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-700">
            {t("why.subtitle")}
          </p>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { key: "b1", Icon: Database },
              { key: "b2", Icon: Layers },
              { key: "b3", Icon: FileSearch },
              { key: "b4", Icon: LineChart },
            ].map(({ key, Icon }) => (
              <div
                key={key}
                data-testid={`why-card-${key}`}
                className="flex h-full flex-col rounded-lg border border-slate-200 bg-white p-6 shadow-sm hover:border-[#C85A32]"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#0A2540] text-white">
                  <Icon size={18} />
                </span>
                <h3 className="mt-5 font-serif text-lg font-semibold text-[#0A2540]">
                  {t(`why.${key}.title`)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  {t(`why.${key}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT IMAGE */}
      <section className="bg-white px-4 py-20 sm:px-6 lg:px-10">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm">
            <img
              src="/images/vaca-muerta.jpg"
              alt="Vaca Muerta"
              className="h-72 w-full object-cover md:h-96"
            />
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm">
            <img
              src="/images/salt-flats.jpg"
              alt="Lithium salt flats — Salta / Jujuy"
              className="h-72 w-full object-cover md:h-96"
            />
          </div>
        </div>
      </section>

      {/* CIRCUIT */}
      <section id="circuit" className="bg-[#0A2540] px-4 py-20 text-white sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <p className="font-mono text-xs uppercase tracking-widest text-[#F4A582]">
            {t("circuit.eyebrow")}
          </p>
          <h2 className="mt-3 max-w-3xl font-serif text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            {t("circuit.title")}
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-300">
            {t("circuit.subtitle")}
          </p>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {["s1", "s2", "s3", "s4"].map((k, idx) => (
              <div
                key={k}
                data-testid={`circuit-step-${k}`}
                className="rounded-lg border-t-4 border-[#C85A32] bg-white/5 p-6 backdrop-blur-sm"
              >
                <p className="font-mono text-3xl font-bold text-[#C85A32]">
                  {String(idx + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 font-serif text-lg font-semibold">
                  {t(`circuit.${k}.title`)}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-300">
                  {t(`circuit.${k}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="bg-white px-4 py-20 sm:px-6 lg:px-10">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm">
              <img
                src="/images/monitoring.jpg"
                alt=""
                className="h-72 w-full object-cover md:h-full"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-widest text-[#C85A32]">
              {t("transparency.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight text-[#0A2540] sm:text-3xl lg:text-4xl">
              {t("transparency.title")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-700">
              {t("transparency.desc")}
            </p>
            <p className="mt-3 text-base leading-relaxed text-slate-700">
              {t("transparency.desc2")}
            </p>

            <ul className="mt-6 space-y-4">
              {["point1", "point2", "point3"].map((p) => (
                <li key={p} data-testid={`transparency-${p}`} className="flex gap-3">
                  <ShieldCheck size={20} className="mt-0.5 shrink-0 text-[#C85A32]" />
                  <div>
                    <p className="font-semibold text-[#0A2540]">
                      {t(`transparency.${p}_title`)}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">
                      {t(`transparency.${p}_desc`)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-[#F8FAFC] px-4 py-20 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-4xl rounded-xl border border-slate-700 bg-[#0A2540] p-8 text-white shadow-2xl md:p-12">
          <p className="font-mono text-xs uppercase tracking-widest text-[#F4A582]">
            {t("newsletter.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-2xl font-bold tracking-tight sm:text-3xl">
            {t("newsletter.title")}
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
            {t("newsletter.subtitle")}
          </p>
          <div className="mt-8">
            <NewsletterForm />
          </div>
        </div>
      </section>
    </main>
  );
}
